import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { axiosInstance } from '../helpers'
import { Service } from '../models/Services/types'

// Simple Vendor type for the COI
export interface SimpleVendor {
    id: number
    name: string
    email?: string
}

// Compliance status enum
export type VendorCOIStatus =
    | 'PENDING_REVIEW'
    | 'COMPLIANT'
    | 'NON_COMPLIANT'
    | 'EXPIRED'
    | 'EXPIRING_SOON'
    | 'NOT_YET_EFFECTIVE'

// Compliance check response types
export interface FieldComparison {
    field: string
    display_name: string
    required: number | string | null
    actual: number | string | null
    compliant: boolean
}

export interface ComplianceResult {
    is_compliant: boolean
    issues: string[]
    field_comparisons: FieldComparison[]
    status: VendorCOIStatus
    matching_samples: string[]
    strictest_requirements: Record<string, number | string | boolean | null>
}

export interface ComplianceCheckResponse {
    vendor_coi: VendorCOI
    compliance: ComplianceResult
}

// VendorCOI type based on the Django serializer
export interface VendorCOI {
    id: number
    apartment: number
    apartment_name: string
    apartment_id: number
    vendor: SimpleVendor | null
    file: string
    name: string
    services: Service[]
    effective_date: string | null
    expiration_date: string | null
    // Certificate Holder
    certificate_holder_name: string | null
    certificate_holder_address: string | null
    // Description of Operations
    description_of_operations: string | null
    // General Liability
    gl_each_occurrence: string | null
    gl_damage_to_rented_premises: string | null
    gl_med_exp: string | null
    gl_personal_adv_injury: string | null
    gl_general_aggregate: string | null
    gl_products_comp_op_agg: string | null
    // Auto Liability
    auto_combined_single_limit: string | null
    auto_bodily_injury_per_person: string | null
    auto_bodily_injury_per_accident: string | null
    auto_property_damage: string | null
    // Umbrella
    umbrella_each_occurrence: string | null
    umbrella_aggregate: string | null
    // Workers Comp
    wc_statutory_limits: boolean | null
    wc_el_each_accident: string | null
    wc_el_disease_ea_employee: string | null
    wc_el_disease_policy_limit: string | null
    // Compliance
    status: VendorCOIStatus
    compared_to_sample: number | null
    compliance_notes: string | null
    ai_summary: string | null
    uploaded_by: number
    reviewed_by: { id: number; name: string; email: string } | null
    reviewed_at: string | null
    created_date: string
    modified_date: string
}

export interface UpdateVendorCOIRequest {
    name?: string
    vendor_id?: number
    service_ids?: number[]
    effective_date?: string
    expiration_date?: string
    // Certificate Holder
    certificate_holder_name?: string
    certificate_holder_address?: string
    // Description of Operations
    description_of_operations?: string
    // General Liability
    gl_each_occurrence?: string
    gl_damage_to_rented_premises?: string
    gl_med_exp?: string
    gl_personal_adv_injury?: string
    gl_general_aggregate?: string
    gl_products_comp_op_agg?: string
    // Auto Liability
    auto_combined_single_limit?: string
    auto_bodily_injury_per_person?: string
    auto_bodily_injury_per_accident?: string
    auto_property_damage?: string
    // Umbrella
    umbrella_each_occurrence?: string
    umbrella_aggregate?: string
    // Workers Comp
    wc_statutory_limits?: boolean
    wc_el_each_accident?: string
    wc_el_disease_ea_employee?: string
    wc_el_disease_policy_limit?: string
}

export interface OrgVendorCOIParams {
    organization?: number | null
    vendor?: number
    status?: VendorCOIStatus
}

const QUERY_KEY = ['orgVendorCOIs']

export const useOrgVendorCOIs = (params?: OrgVendorCOIParams) => {
    const queryClient = useQueryClient()
    const [processingIds, setProcessingIds] = useState<Set<number>>(new Set())

    // Build query string for list endpoints (includes all filters)
    const buildQueryString = () => {
        if (!params) return ''
        const searchParams = new URLSearchParams()
        if (params.organization)
            searchParams.append('organization', params.organization.toString())
        if (params.vendor)
            searchParams.append('vendor', params.vendor.toString())
        if (params.status) searchParams.append('status', params.status)
        const queryString = searchParams.toString()
        return queryString ? `?${queryString}` : ''
    }

    // Build query string for detail mutations (organization only, no list filters)
    const buildDetailQueryString = () => {
        if (!params?.organization) return ''
        return `?organization=${params.organization}`
    }

    // Get all vendor COIs for organization
    const {
        data: vendorCOIs,
        isLoading: isLoadingVendorCOIs,
        error: errorVendorCOIs,
        refetch: refetchVendorCOIs,
    } = useQuery<AxiosResponse<VendorCOI[]>, Error>({
        queryKey: [...QUERY_KEY, params],
        queryFn: () =>
            axiosInstance.get(`/company/vendor_coi/${buildQueryString()}`),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        enabled: !!params?.organization,
    })

    // Process/analyze a vendor COI with AI
    const processVendorCOI = useMutation({
        mutationFn: async (vendorCOIId: number) => {
            setProcessingIds((prev) => new Set(prev).add(vendorCOIId))
            return axiosInstance.post<VendorCOI>(
                `/company/vendor_coi/${vendorCOIId}/process_coi/${buildDetailQueryString()}`,
            )
        },
        onSuccess: (_, vendorCOIId) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(vendorCOIId)
                return next
            })
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Vendor COI analyzed successfully')
        },
        onError: (error: any, vendorCOIId) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(vendorCOIId)
                return next
            })
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to analyze vendor COI'
            toast.error(errorMessage)
        },
    })

    // Auto check compliance for a vendor COI
    const autoCheckCompliance = useMutation({
        mutationFn: async (vendorCOIId: number) => {
            return axiosInstance.post<ComplianceCheckResponse>(
                `/company/vendor_coi/${vendorCOIId}/auto_check_compliance/${buildDetailQueryString()}`,
            )
        },
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            const { compliance } = response.data
            if (compliance.is_compliant) {
                toast.success('COI is compliant!')
            } else {
                toast.warning(
                    `Non-compliant: ${compliance.issues.length} issue(s) found`,
                )
            }
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to check compliance'
            toast.error(errorMessage)
        },
    })

    // Manually set compliance status for a vendor COI
    const setComplianceStatus = useMutation({
        mutationFn: async ({
            vendorCOIId,
            status,
            notes,
        }: {
            vendorCOIId: number
            status: 'COMPLIANT' | 'NON_COMPLIANT'
            notes?: string
        }) => {
            return axiosInstance.post<VendorCOI>(
                `/company/vendor_coi/${vendorCOIId}/set_compliance_status/${buildDetailQueryString()}`,
                { status, notes },
            )
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success(
                variables.status === 'COMPLIANT'
                    ? 'Marked as compliant'
                    : 'Marked as non-compliant',
            )
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to set compliance status'
            toast.error(errorMessage)
        },
    })

    // Update a vendor COI
    const updateVendorCOI = useMutation({
        mutationFn: async ({
            id,
            data,
        }: {
            id: number
            data: UpdateVendorCOIRequest
        }) => {
            return axiosInstance.patch<VendorCOI>(
                `/company/vendor_coi/${id}/${buildDetailQueryString()}`,
                data,
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Vendor COI updated successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to update vendor COI'
            toast.error(errorMessage)
        },
    })

    // Delete a vendor COI
    const deleteVendorCOI = useMutation({
        mutationFn: async (id: number) => {
            return axiosInstance.delete(
                `/company/vendor_coi/${id}/${buildDetailQueryString()}`,
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Vendor COI deleted successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to delete vendor COI'
            toast.error(errorMessage)
        },
    })

    return {
        vendorCOIs: vendorCOIs?.data || [],
        isLoadingVendorCOIs,
        errorVendorCOIs,
        refetchVendorCOIs,
        processVendorCOI,
        autoCheckCompliance,
        setComplianceStatus,
        updateVendorCOI,
        deleteVendorCOI,
        isProcessing: processVendorCOI.isLoading,
        isUpdating: updateVendorCOI.isLoading,
        isDeleting: deleteVendorCOI.isLoading,
        isCheckingCompliance: autoCheckCompliance.isLoading,
        isSettingComplianceStatus: setComplianceStatus.isLoading,
        processingIds,
    }
}
